import { memo, useEffect, useMemo, useState } from 'react'
import {
  BgBox,
  DataDashboardContent,
  DataDashboardTitle,
  DataDashboardWrap,
  DataItem,
  DataItemTag,
  DataItemTitle,
  DataItemValue,
  PageWrap,
  TasksList,
  TasksWrap,
  Title,
  TitleIconWrap,
  TotalVolume,
} from './style'
import { MainWrap } from 'views/Main/styles'
import { Nav } from 'views/Nav'
import { ReactComponent as TitleBg } from './assets/title-bg.svg'
import { ReactComponent as TitleStar } from './assets/Star.svg'

import { UserStatus, getUserV2Info } from 'api/v1/account'
import { useAccountInfo } from 'store/account/hooks'
import dayjs from 'dayjs'
import { useCurrentThemeType } from 'store/settings/hooks'
import { Stack } from '@mui/material'
import { AirdropItem, getAirdrops } from 'api/v1/airdrop'
import { useWeb3Address } from 'store/web3/hooks'
import { toGrouping } from 'utils/number'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { IconInfo } from 'components/Icons'

const AccountStatus = memo(() => {
  const [airdrops, setAirdrops] = useState<AirdropItem[]>([])
  const web3Address = useWeb3Address()

  const [userData, setData] = useState<UserStatus>({} as UserStatus)

  useEffect(() => {
    if (web3Address) {
      getUserV2Info(web3Address).then((res) => {
        setData(res.data ?? {})
      })
    }
  }, [web3Address])

  const format = (n: number | string) => {
    return Number(n ?? 0).toLocaleString()
  }

  const formatTime = (n: number) => {
    return dayjs(n).format('D MMM YYYY')
  }

  const registerDays = useMemo(() => {
    if (userData.registerTime) {
      return Math.ceil(
        (Date.now() - userData.registerTime) / (24 * 60 * 60 * 1000),
      )
    }
    return '0'
  }, [userData.registerTime])

  useEffect(() => {
    if (!web3Address) return
    getAirdrops(web3Address).then((r) => {
      setAirdrops(r.data || [])
    })
  }, [web3Address, setAirdrops])

  const total = airdrops.reduce((acc, item) => acc + item.reward, 0)

  const isLocked = airdrops?.length
    ? airdrops.some((item) => item.isLocked)
    : true

  return (
    <MainWrap sx={{ position: 'relative' }}>
      <Nav />
      <PageWrap>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={'space-between'}
          sx={{
            marginTop: '64px',
          }}
        >
          <Title>
            $OG Rewards
            <TitleIconWrap>
              <TitleBg
                className="wrap"
                style={{ width: '350px', height: '86px' }}
              />
              <TitleStar
                className="star"
                style={{ width: '36px', height: '36px' }}
              />
            </TitleIconWrap>
          </Title>
          <TotalVolume>
            <span>Total Points</span>
            <div className="value">
              <b
                className={clsx({
                  locked: isLocked,
                })}
              >
                {toGrouping(total, 2)}
              </b>
            </div>
            <div className="tips">
              <IconInfo />
              Your rewards await! Visit{' '}
              <Link
                to="https://tradoor.io/rewards?source=migrate"
                target="_blank"
              >
                Tradoor
              </Link>{' '}
              in one click.
            </div>
          </TotalVolume>
        </Stack>
        <TasksWrap>
          <div className="head">Rewards</div>
          <TasksList>
            {airdrops?.map((item) => (
              <div key={item.id} className="item">
                <div className="title">{item.title}</div>
                <div className="total">
                  <b
                    className={clsx({
                      locked: item.isLocked,
                    })}
                  >
                    {toGrouping(Math.floor(item.reward), 2)}
                  </b>
                </div>
              </div>
            ))}
            <div className="item">
              <div className="coming">Coming soon...</div>
              <div className="total"></div>
            </div>
          </TasksList>
        </TasksWrap>
        <DataDashboardWrap>
          <DataDashboardTitle>V2 Account Activity</DataDashboardTitle>
          <DataDashboardContent>
            <DataItem>
              <DataItemTitle>Total Trades</DataItemTitle>
              <DataItemValue>$ {format(userData.tradeAmount)}</DataItemValue>
              <DataItemTag>{format(userData.tradeCount)} trades</DataItemTag>
            </DataItem>
            <DataItem>
              <DataItemTitle>Convert</DataItemTitle>
              <DataItemValue>$ {format(userData.convertAmount)}</DataItemValue>
              <DataItemTag>{format(userData.convertCount)} trades</DataItemTag>
            </DataItem>
            <DataItem>
              <DataItemTitle>SPOT</DataItemTitle>
              <DataItemValue>$ {format(userData.spotAmount)}</DataItemValue>
              <DataItemTag>{format(userData.spotCount)} trades</DataItemTag>
            </DataItem>
            <DataItem>
              <DataItemTitle>Deposits</DataItemTitle>
              <DataItemValue>$ {format(userData.depositAmount)}</DataItemValue>
              <DataItemTag>{format(userData.depositCount)} times</DataItemTag>
            </DataItem>
            <DataItem>
              <DataItemTitle>Withdrawals</DataItemTitle>
              <DataItemValue>$ {format(userData.withdrawAmount)}</DataItemValue>
              <DataItemTag>{format(userData.withdrawCount)} times</DataItemTag>
            </DataItem>
            <DataItem>
              <DataItemTitle>Account opened</DataItemTitle>
              <DataItemValue>
                {userData.registerTime
                  ? formatTime(userData.registerTime)
                  : '-'}
              </DataItemValue>
              <DataItemTag>Active {registerDays} days</DataItemTag>
            </DataItem>
          </DataDashboardContent>
        </DataDashboardWrap>
      </PageWrap>
      <BgBox />
    </MainWrap>
  )
})

export default AccountStatus
